import type {
  CustomerCatalogueOption,
  CustomerCatalogueOptionChoice,
} from "@shared-core/data/customer-api-types";
import {
  isPaperTypeUnavailable,
  optionForPaperType,
  paperSplit,
} from "@shared-core/domain/catalogue-paper-choice";
import { CUSTOM_OPTION_SENTINEL } from "@shared-core/domain/catalogue-configuration";
import { customerLabelOf } from "@shared-core/domain/customer-option-wording";

// How the item page decides which option groups are real customer choices and
// which are tucked away. Pure, so the rules are unit-testable.

/**
 * Option keys that are production or compliance choices, not what the customer
 * is buying: they sit under the "More options" disclosure, collapsed.
 *
 * Routing inputs (ProductionMethod, ColourSpecification, anything the
 * dictionary marks `routing_input`) are not here because the customer API never
 * sends them: the server drops them from the item and sets them itself
 * (`item_to_customer_detail`, `pin_routing_inputs`). What is left is a frontend
 * list only because that API still drops each field's `mode` and `section`; it
 * should become a backend flag on the customer option schema, and until then a
 * new field of this character needs its key adding here. Keys are the
 * dictionary's field keys (modules/dictionary_module/item_files): Certification
 * and FscLogoPosition are `_certification_fields`, Proofing and Packing are on
 * nearly every item, and FileCopy is the file-copy charge beside them.
 */
export const MORE_OPTIONS_KEYS: ReadonlySet<string> = new Set([
  "Proofing",
  "Certification",
  "FscLogoPosition",
  "Packing",
  "FileCopy",
]);

/**
 * A group with this many choices or fewer is shown as tiles; with more it is a
 * labelled dropdown. Brad, 2 Oct 2026: options are tiles, not dropdowns. Brad,
 * 12 Oct 2026: the tile page was too long (the business card's buy box was
 * about 3,100px), so tiles for up to 4 choices (two rows at the buy box's
 * width) and dropdowns for 5 or more.
 */
export const TILE_MAX_OPTIONS = 4;

/** A group offering no real choice: one option (a fixed field reaches the customer as exactly this). */
export function isSingleChoice(option: CustomerCatalogueOption): boolean {
  return option.choices.length <= 1;
}

export interface OptionGroupLayout {
  /** Groups shown in the page flow, in page order: as tiles, or a muted line when single-choice. */
  inline: CustomerCatalogueOption[];
  /** Production and compliance groups with a real choice, for the "More options" disclosure. */
  more: CustomerCatalogueOption[];
}

export function layOutOptionGroups(
  options: readonly CustomerCatalogueOption[],
): OptionGroupLayout {
  const inline: CustomerCatalogueOption[] = [];
  const more: CustomerCatalogueOption[] = [];
  for (const option of options) {
    if (MORE_OPTIONS_KEYS.has(option.key) && !isSingleChoice(option)) {
      more.push(option);
    } else {
      inline.push(option);
    }
  }
  return { inline, more };
}

/**
 * A paper group as two groups (R204): its paper types, then the chosen type's
 * weights only; a named stock is a type of its own with no weights. Each is drawn like any other group; the value stays the one
 * paper code. Null when the group is not all papers, or a split would not help
 * (`paperSplit`).
 */
export interface PaperGroups {
  /** The types as choices, coded by type; its value is the selected paper's type. */
  types: CustomerCatalogueOption;
  typeValue: string;
  /** Types every weight of which the rest of the selection rules out. */
  unavailableTypes: ReadonlySet<string>;
  /** The type holding the most popular paper, for its badge. */
  mostPopularType: string | undefined;
  /** The paper code a type choice selects: the same weight where it has it. */
  codeForType: (typeCode: string) => string;
  /** The chosen type's papers, labelled by weight; absent until a paper type is chosen. */
  weights: CustomerCatalogueOption | null;
  /**
   * The paper the customer types, when the group takes one (spec 32 §5.7): its
   * choice is the last type, and while it is chosen there are no weights. Null
   * when the group takes none.
   */
  typed: TypedPaper | null;
}

/** A paper we don't list, typed by the customer and priced by our team. */
export interface TypedPaper {
  label: string;
  hint: string | null;
  maxLength: number | null;
  /** Whether what they type is searched for on our stock list (manual RFQ slice 4). */
  hasStockList: boolean;
  /** Whether it is the chosen type. */
  isChosen: boolean;
  /** What they have typed so far; empty until they type. */
  text: string;
}

/** The type code the typed paper's choice carries: no paper type has it. */
export const TYPED_PAPER_TYPE = "__typed_paper__";

/**
 * The value a typed paper group holds: the text, or, chosen but not yet
 * typed, the sentinel (never sent as a paper).
 */
export function typedPaperValue(text: string): string {
  return text.trim() ? text : CUSTOM_OPTION_SENTINEL;
}

/** Whether the group's value is a typed paper still to be typed: the line can't be added yet. */
export function isTypedPaperPending(
  option: CustomerCatalogueOption,
  value: string,
): boolean {
  return Boolean(option.typed_choice) && value === CUSTOM_OPTION_SENTINEL;
}

export function paperGroupsOf(
  option: CustomerCatalogueOption,
  value: string,
  unavailableCodes: ReadonlySet<string> | undefined,
  mostPopularCode: string | undefined,
): PaperGroups | null {
  const papers = option.choices.map((choice) => ({
    code: choice.code,
    label: choice.label,
    isDefault: choice.code === option.default_code,
    choice,
    paper: choice.paper
      ? {
          typeCode: choice.paper.type_code,
          typeLabel: choice.paper.type_label,
          weight: choice.paper.weight,
          weightLabel: choice.paper.weight_label,
        }
      : null,
  }));
  const split = paperSplit(papers, value);
  if (!split) return null;
  const current = papers.find((p) => p.code === value);
  const groupLabel = customerLabelOf(option);
  const typedChoice = option.typed_choice ?? null;
  const isTyped = Boolean(typedChoice) && !current && value !== "";
  const typed: TypedPaper | null = typedChoice
    ? {
        label: typedChoice.label,
        hint: typedChoice.hint ?? null,
        maxLength: typedChoice.max_length ?? null,
        hasStockList: typedChoice.has_stock_list ?? false,
        isChosen: isTyped,
        text: isTyped && value !== CUSTOM_OPTION_SENTINEL ? value : "",
      }
    : null;
  const typedTypeChoice: CustomerCatalogueOptionChoice[] = typedChoice
    ? [
        {
          code: TYPED_PAPER_TYPE,
          label: typedChoice.label,
          customer_label: typedChoice.label,
          customer_hint: null,
        },
      ]
    : [];
  return {
    typed,
    types: {
      ...option,
      key: `${option.key}-type`,
      choices: [
        ...split.types.map((type) => {
          const { choice } = type.options[0];
          // A named stock reads as its own wording; a paper type as its type's.
          return choice.paper
            ? {
                code: type.code,
                label: type.label,
                customer_label: choice.paper.type_customer_label,
                customer_hint: choice.paper.type_customer_hint ?? null,
              }
            : { ...choice, paper: null };
        }),
        ...typedTypeChoice,
      ],
      typed_choice: null,
      default_code: null,
    },
    typeValue: isTyped ? TYPED_PAPER_TYPE : (split.selectedType?.code ?? ""),
    unavailableTypes: new Set(
      split.types
        .filter((type) => isPaperTypeUnavailable(type, unavailableCodes))
        .map((type) => type.code),
    ),
    mostPopularType: split.types.find((type) =>
      type.options.some((p) => p.code === mostPopularCode),
    )?.code,
    codeForType: (typeCode) => {
      if (typeCode === TYPED_PAPER_TYPE) return CUSTOM_OPTION_SENTINEL;
      const type = split.types.find((t) => t.code === typeCode);
      return type
        ? optionForPaperType(type, current, unavailableCodes).code
        : value;
    },
    weights:
      !isTyped && split.selectedType && !split.selectedType.isNamedStock
        ? {
            key: `${option.key}-weight`,
            label: `${option.label} weight`,
            customer_label: `${groupLabel} weight`,
            choices: split.selectedType.options.map(
              (p): CustomerCatalogueOptionChoice => ({
                ...p.choice,
                customer_label: p.paper!.weightLabel,
                customer_hint: null,
                paper: null,
              }),
            ),
            default_code: null,
          }
        : null,
  };
}

/** Whether a group is shown as a tile radiogroup rather than a dropdown. */
export function isTileGroup(option: CustomerCatalogueOption): boolean {
  return option.choices.length <= TILE_MAX_OPTIONS;
}

/**
 * A group whose current selection can't be ordered: ruled out by the rest of
 * the selection (R28), or matching none of the group's choices. A hidden group
 * in this state must be brought to the customer's attention.
 */
export function needsAttention(
  option: CustomerCatalogueOption,
  selectedCode: string,
  unavailableCodes: ReadonlySet<string> | undefined,
): boolean {
  if (unavailableCodes?.has(selectedCode)) return true;
  return !option.choices.some((c) => c.code === selectedCode);
}

/** "Proofing: PDF proof · Certification: None", for the closed disclosure. */
export function summariseSelections(
  options: readonly CustomerCatalogueOption[],
  configuration: Record<string, string>,
): string {
  return options
    .map((option) => {
      const code = configuration[option.key] ?? option.default_code ?? "";
      const choice = option.choices.find((c) => c.code === code);
      return `${customerLabelOf(option)}: ${choice ? customerLabelOf(choice) : "Needs a choice"}`;
    })
    .join(" · ");
}

/**
 * The configuration as the customer-visible choices only, for the line under
 * the gallery: no locked single-choice groups, none of the "More options"
 * groups, and no "None" values.
 */
export function visibleSpecLine(
  options: readonly CustomerCatalogueOption[],
  configuration: Record<string, string>,
): string {
  return layOutOptionGroups(options)
    .inline.filter((option) => !isSingleChoice(option))
    .flatMap((option) => {
      const code = configuration[option.key] ?? option.default_code ?? "";
      const choice = option.choices.find((c) => c.code === code);
      const isNone = choice?.code === "none" || choice?.label === "None";
      return choice && !isNone ? [customerLabelOf(choice)] : [];
    })
    .join(" · ");
}
